/**
 * Dice rolling for the combat engine.
 * Minimal subset from server dice.ts — self-contained, no server imports.
 */

export type DiceResult = 'fail' | 'struggle' | 'success' | 'critical';

export interface DicePoolProbabilities {
  fail: number;
  struggle: number;
  success: number;
  critical: number;
}

/**
 * Calculate exact outcome probabilities for a dice pool of size N.
 * Uses the "highest die" mechanic: fail ≤3, struggle 4-5, success = exactly one 6, critical = 2+ sixes.
 */
export function calculateProbabilities(poolSize: number): DicePoolProbabilities {
  const n = Math.max(1, poolSize);
  const pAllLow = Math.pow(1 / 2, n);           // all dice ≤ 3
  const pNoSix = Math.pow(5 / 6, n);             // all dice ≤ 5
  const pExactlyOneSix = n * (1 / 6) * Math.pow(5 / 6, n - 1); // exactly one 6

  return {
    fail: pAllLow,
    struggle: pNoSix - pAllLow,
    success: pExactlyOneSix,
    critical: 1 - pNoSix - pExactlyOneSix,
  };
}

export interface RollOutcome {
  dice: number[];
  highest: number;
  result: DiceResult;
  hasCritical: boolean;
  /** True when the attack was stopped by the target's defense gate (armor deflection) */
  deflected?: boolean;
}

/** Roll a single d6 */
export function rollD6(): number {
  return Math.floor(Math.random() * 6) + 1;
}

/** Evaluate a set of dice rolls into a result.
 *  When difficulty > 0, the roll must have at least (difficulty + 1) dice
 *  showing 4+ to pass — otherwise it's a fail with deflected: true.
 */
export function evaluateRoll(dice: number[], difficulty: number = 0): RollOutcome {
  const highest = Math.max(...dice);
  const sixes = dice.filter(d => d === 6).length;
  const hasCritical = sixes >= 2;

  // Defense gate: need (difficulty + 1) qualifying dice showing 4+
  if (difficulty > 0) {
    const qualifying = dice.filter(d => d >= 4).length;
    if (qualifying < difficulty + 1) {
      return { dice, highest, result: 'fail', hasCritical: false, deflected: true };
    }
  }

  let result: DiceResult;
  if (hasCritical) {
    result = 'critical';
  } else if (highest >= 6) {
    result = 'success';
  } else if (highest >= 4) {
    result = 'struggle';
  } else {
    result = 'fail';
  }

  return { dice, highest, result, hasCritical };
}
